import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, InfoWindow, Marker, Polygon, Polyline, useJsApiLoader } from '@react-google-maps/api';
import * as mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Spin } from 'antd';
import {
    AOI_DETAIL_COLORS,
    DEFAULT_DETAIL_ZOOM,
    FIT_BOUNDS_MAX_ZOOM,
    FIT_BOUNDS_PADDING_PX,
    GOOGLE_MAPS_API_KEY,
    LayerVisibility,
    MAPBOX_ACCESS_TOKEN,
    MAPBOX_OSM_STYLE,
    MapProvider,
    PickTarget,
    TempPickedPoint,
} from '../constants';
import {
    BizAoiAttributeChildAoi,
    BizAoiAttributeDetail,
    BizAoiAttributeGeometry,
    BizAoiAttributeLocationPoint,
    BizAoiAttributeRelatedAddress,
} from '../graphql/queries/getAoiAttributeDetail';

// Mapbox 全局 token (跟 AddressLocationReviewDrawer 保持一致, 模块加载时一次注入).
// 用 `as any` 是因为 `import * as mapboxgl` 拿到的是模块 namespace 类型, 它本身不带可写 accessToken 字段;
// mapbox-gl 在运行时确实会把这个属性挂在模块对象上, TS 严格类型断言会冲突, 这里跟项目其它地图代码保持一致。
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(mapboxgl as any).accessToken = MAPBOX_ACCESS_TOKEN;

// Mapbox 默认 popup tip 三角带边框 + 内容容器有内边距, 视觉跟我们其他卡片不统一。
// 把 .aoi-popup 这个 className 下的样式覆写一下: 纯白底, 不要 border, 加阴影, 圆角, 紧凑 padding。
// 也覆写 X 关闭按钮样式 + 加 click 处理 (用事件委托, 监听 document 上点 .aoi-popup-close).
const AOI_POPUP_STYLE_ID = '__aoi_popup_style__';
if (typeof document !== 'undefined' && !document.getElementById(AOI_POPUP_STYLE_ID)) {
    const style = document.createElement('style');
    style.id = AOI_POPUP_STYLE_ID;
    style.textContent = `
        .aoi-popup .mapboxgl-popup-content {
            background: #ffffff;
            border-radius: 8px;
            border: none;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.06);
            padding: 12px 14px;
        }
        .aoi-popup .mapboxgl-popup-tip {
            border-top-color: #ffffff;
            border-bottom-color: #ffffff;
            border-left-color: #ffffff;
            border-right-color: #ffffff;
            filter: drop-shadow(0 1px 1px rgba(0,0,0,0.06));
        }
        .aoi-popup-close {
            border: none; background: transparent; cursor: pointer;
            color: #9ca3af; font-size: 18px; line-height: 1; padding: 0;
            flex-shrink: 0; width: 18px; height: 18px;
            display: inline-flex; align-items: center; justify-content: center;
        }
        .aoi-popup-close:hover { color: #374151; }
    `;
    document.head.appendChild(style);
}
/**
 * Popup 创建后给 .aoi-popup-close 这个 X 按钮挂上 click → popup.remove()。
 * 调用方在 popup.setHTML + setLngLat + addTo 之后立刻调一下。
 */
function wirePopupCloseButton(popup: mapboxgl.Popup) {
    const closeBtn = popup.getElement()?.querySelector('.aoi-popup-close') as HTMLButtonElement | null;
    closeBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        popup.remove();
    });
}

const containerStyle = { width: '100%', height: '100%' };

interface Props {
    detail: BizAoiAttributeDetail;
    provider: MapProvider;
    layerVisibility: LayerVisibility;
    onProviderChange: (p: MapProvider) => void;
    /**
     * 当前在地图上渲染的进门点列表。
     * <p>view 模式: 父组件传 `detail.attribute.entryPoints`;
     * <p>edit 模式: 父组件传 `draft.entryPoints` (实时反映用户改动, 包括未保存的新增 / 删除)。
     */
    entryPoints?: BizAoiAttributeLocationPoint[];
    /** 同 entryPoints, 停车点版本。 */
    parkingPoints?: BizAoiAttributeLocationPoint[];
    /**
     * 编辑模式下的拾取目标 (null = 不在拾取中)。非 null 时:
     * - 鼠标变 crosshair
     * - 地图 click → 调 onPickClick 抛回点击坐标
     * - 已有临时坐标 (tempPoint) 时, 在地图上画临时 marker
     */
    pickTarget?: PickTarget;
    /** 临时点位 (用户已点击地图但还没确定加入列表)。 */
    tempPoint?: TempPickedPoint | null;
    /** 用户在地图上点了一下 (或拖动了临时 marker) 时回调。 */
    onPickClick?: (lng: number, lat: number) => void;
    /**
     * 是否允许拖动已保存的进门 / 停车点 marker (edit 模式才打开)。
     * 拖完后通过 {@link onPointDrag} 回写新坐标。
     */
    editable?: boolean;
    /**
     * 拖动已保存点 marker dragend 时回调。
     * @param target  哪类点 (entryPoints / parkingPoints)
     * @param idx     在该列表里的位置
     * @param lng/lat 新坐标
     */
    onPointDrag?: (target: 'entryPoints' | 'parkingPoints', idx: number, lng: number, lat: number) => void;
    /**
     * 地址级点位"按地址查看"选中的 address_core_id (null=不高亮)。
     * 非 null 时: 该地址自己的进门/停车点在图上以实心大点+光环高亮, 聚合层淡化,
     * 让运营看清"这个进门点属于哪个地址"。
     */
    selectedAddressId?: number | null;
}

/**
 * 详情页地图组件 - 同时渲染 Google Maps 和 Mapbox, 通过 display:block/none 切换。
 *
 * <p>风格参考: 现有 AddressLocationReviewDrawer.tsx 的双底图模式。
 *
 * <p>渲染元素 (按 z-order 从下往上):
 * <ul>
 *   <li>周边 AOI polygon (灰色, 受 showNearby 控制)</li>
 *   <li>子 AOI polygon (青色, 2级 AOI + 受 showChildren 控制)</li>
 *   <li>自身 AOI polygon (紫色高亮, 始终显示)</li>
 *   <li>关联地址 marker (红色, 1级 AOI + 受 showRelatedAddresses 控制)</li>
 *   <li>停车点 marker (橙色, 受 showParkingPoints 控制)</li>
 *   <li>进门点 marker (绿色, 受 showEntryPoints 控制)</li>
 *   <li>临时拾取 marker (半透明大圆, 仅 pickTarget !== null 时)</li>
 * </ul>
 */
const AoiDetailMap: React.FC<Props> = ({
    detail,
    provider,
    layerVisibility,
    onProviderChange,
    entryPoints,
    parkingPoints,
    pickTarget = null,
    tempPoint = null,
    onPickClick,
    editable = false,
    onPointDrag,
    selectedAddressId = null,
}) => {
    // 不传 props 时回退到 detail (保持向后兼容: view 模式可以不传, 沿用之前的渲染逻辑)。
    // useMemo 保证引用稳定 — 否则下面 Mapbox 渲染 effect 每次 render 都跑一遍 renderMapboxLayers。
    const effectiveEntryPoints = useMemo(
        () => entryPoints ?? detail.attribute?.entryPoints ?? [],
        [entryPoints, detail.attribute?.entryPoints],
    );
    const effectiveParkingPoints = useMemo(
        () => parkingPoints ?? detail.attribute?.parkingPoints ?? [],
        [parkingPoints, detail.attribute?.parkingPoints],
    );
    const center = useMemo(() => ({
        lat: detail.centerLatitude ?? 0,
        lng: detail.centerLongitude ?? 0,
    }), [detail.centerLatitude, detail.centerLongitude]);

    // 关联地址按 (lat, lng) 聚合: 一栋楼可能有几百个 unit 共享同一坐标, 渲染 374 个 marker 既慢又看不出区别;
    // 聚合后一个坐标只一个 marker, 点击展开 popup 看明细。坐标按 7 位精度 (≈ 1cm) 当 key, 防止浮点抖动散开。
    const addressClusters = useMemo(
        () => clusterAddressesByCoord(detail.relatedAddresses),
        [detail.relatedAddresses],
    );

    // 地址级点位 (每个关联地址自己的进门/停车点, 与楼级 attribute 点位是两层数据) 同样按坐标聚合:
    // 多个 unit 的地址级点位常常落在同一坐标, popup 里列出这个点归属哪些地址。
    const addressPointClusters = useMemo(
        () => clusterAddressPointsByCoord(detail.relatedAddresses),
        [detail.relatedAddresses],
    );

    // "按地址查看"选中地址自己的点位(高亮用): 实心大点 + 光环, 聚合层此时淡化
    const highlightedAddressPoints = useMemo<HighlightPoint[]>(() => {
        if (selectedAddressId == null) return [];
        const addr = detail.relatedAddresses.find((a) => a.addressCoreId === selectedAddressId);
        if (!addr) return [];
        const out: HighlightPoint[] = [];
        for (const p of addr.entryPoints ?? []) {
            if (p.lng != null && p.lat != null) out.push({ kind: 'entry', lng: p.lng, lat: p.lat, remark: p.remark });
        }
        for (const p of addr.parkingPoints ?? []) {
            if (p.lng != null && p.lat != null) out.push({ kind: 'park', lng: p.lng, lat: p.lat, remark: p.remark });
        }
        return out;
    }, [selectedAddressId, detail.relatedAddresses]);
    const addressPointsDimmed = selectedAddressId != null;

    // 地址级点位 → 所属地址 的连线(虚线): 每个进门/停车点连回该地址自己的坐标(红点),
    // 让运营一眼看清"这个进门点属于哪个地址"。选中某地址时其连线加粗高亮, 其余淡化。
    const addressConnectors = useMemo<AddrConnector[]>(() => {
        const out: AddrConnector[] = [];
        for (const a of detail.relatedAddresses) {
            if (a.latitude == null || a.longitude == null) continue;
            const pts: AddrConnector['points'] = [];
            for (const p of a.entryPoints ?? []) {
                if (p.lng != null && p.lat != null) pts.push({ kind: 'entry', lng: p.lng, lat: p.lat });
            }
            for (const p of a.parkingPoints ?? []) {
                if (p.lng != null && p.lat != null) pts.push({ kind: 'park', lng: p.lng, lat: p.lat });
            }
            if (pts.length) {
                out.push({ addressCoreId: a.addressCoreId, addrLng: a.longitude, addrLat: a.latitude, points: pts });
            }
        }
        return out;
    }, [detail.relatedAddresses]);

    // 当前在地图上展开的 popup (clusterKey = 坐标字符串); null = 没展开。
    // 切换 provider / 详情刷新 / 编辑模式都会清掉。
    const [activeAddressClusterKey, setActiveAddressClusterKey] = useState<string | null>(null);
    const activeAddressCluster = useMemo(
        () => addressClusters.find((c) => c.key === activeAddressClusterKey) ?? null,
        [addressClusters, activeAddressClusterKey],
    );

    // 进门 / 停车点 marker 当前点开的 popup; null = 没展开。
    // key 用 "entry-<idx>" / "park-<idx>" 编码, 详情切换 / 图层关闭时清掉。
    const [activePointKey, setActivePointKey] = useState<string | null>(null);
    const activePoint = useMemo(() => {
        if (!activePointKey) return null;
        const [kind, idxStr] = activePointKey.split('-');
        const idx = Number(idxStr);
        const point = kind === 'entry' ? effectiveEntryPoints[idx] : effectiveParkingPoints[idx];
        if (!point || point.lng == null || point.lat == null) return null;
        return {
            type: kind === 'entry' ? '进门点' : '停车点',
            color: kind === 'entry' ? AOI_DETAIL_COLORS.entryPoint : AOI_DETAIL_COLORS.parkingPoint,
            point,
        };
    }, [activePointKey, effectiveEntryPoints, effectiveParkingPoints]);

    // 地址级点位 marker 当前点开的 popup; key = cluster.key, null = 没展开。
    const [activeAddressPointKey, setActiveAddressPointKey] = useState<string | null>(null);
    const activeAddressPointCluster = useMemo(
        () => addressPointClusters.find((c) => c.key === activeAddressPointKey) ?? null,
        [addressPointClusters, activeAddressPointKey],
    );

    // 详情切换 / 图层关闭时关掉打开的 popup, 防止显示陈旧数据
    useEffect(() => {
        setActiveAddressClusterKey(null);
    }, [detail.aoiId, layerVisibility.showRelatedAddresses]);
    useEffect(() => {
        setActivePointKey(null);
    }, [detail.aoiId, layerVisibility.showEntryPoints, layerVisibility.showParkingPoints]);
    useEffect(() => {
        setActiveAddressPointKey(null);
    }, [detail.aoiId, layerVisibility.showAddressPoints]);

    /* ====================== Google Maps 加载 ====================== */
    // id 必须跟项目里其他 useJsApiLoader 一致 (AddressLocationReviewDrawer 等用的都是 'google-map-script'),
    // 否则 @react-google-maps/api 的全局 loader 会 throw "must not be called again with different options".
    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    });

    // Google Polygon 数据: GeoJSON.coordinates → google.maps.LatLngLiteral[][] (支持 Polygon / MultiPolygon)
    const selfPolygonPaths = useMemo(() => geomToGooglePaths(detail.selfGeometry), [detail.selfGeometry]);
    const childPolygonPathsList = useMemo(
        () => detail.childAois.map((c) => ({ aoiId: c.aoiId, paths: childAoiToGooglePaths(c) })),
        [detail.childAois],
    );
    const nearbyPolygonPathsList = useMemo(
        () => detail.nearbyGeometries.map((g) => ({ aoiId: g.aoiId, paths: geomToGooglePaths(g) })),
        [detail.nearbyGeometries],
    );

    /* ====================== Google 实例 (用于 fitBounds 等命令式调用) ======================
     *
     * <p>注意: 单独用 ref 不够 — onLoad 写 ref 不会触发 React 重渲, 下面 fitBounds 的 useEffect
     * 在首次 mount 时跑了一次但 ref 还是 null (onLoad 还没 fire), 拿到的 map 是 null 就早返了。
     * 加一个 googleMapReady state, onLoad 时 setTrue, 把它放进 effect deps, 让 effect 在 map 就绪
     * 后再跑一次 — 这是首次进详情 AOI 看着很小没按 padding fit 的根因。
     */
    const googleMapRef = useRef<google.maps.Map | null>(null);
    const [googleMapReady, setGoogleMapReady] = useState(false);

    /* ====================== Mapbox 实例管理 ====================== */
    const mapboxContainerRef = useRef<HTMLDivElement | null>(null);
    const mapboxInstanceRef = useRef<mapboxgl.Map | null>(null);
    const mapboxMarkersRef = useRef<mapboxgl.Marker[]>([]);
    const mapboxTempMarkerRef = useRef<mapboxgl.Marker | null>(null);
    // 当前 mapbox 打开的 popup - 整张地图同一时刻只允许一个 popup; 点新的 marker 时先关旧的, 点地图空白处也关。
    const mapboxActivePopupRef = useRef<mapboxgl.Popup | null>(null);
    // mapbox 的 click handler 用 ref 持有, 方便 effect 卸载时 off 同一个引用
    const mapboxClickHandlerRef = useRef<((e: mapboxgl.MapMouseEvent) => void) | null>(null);

    // ResizeObserver 在 init 时挂上, 容器尺寸变化就触发 resize (修白屏的关键防线)
    const mapboxResizeObserverRef = useRef<ResizeObserver | null>(null);

    /**
     * 命令式初始化 Mapbox - 跟 AddressLocationReviewDrawer 一致的稳定模式, 不走 useEffect。
     *
     * <p><b>为啥不用 useEffect</b>: useEffect 跑在 React commit 之后、浏览器 paint 之前, 容器虽然
     * 已经 display:block 但浏览器 layout 不一定完成 → getBoundingClientRect 拿到 0×0 → Mapbox
     * 在 0×0 canvas 上启动, 之后就算 resize 也救不回来 (内部 projection / tile 计算已经定死)。
     *
     * <p><b>正确的做法</b> (location-correction 验证过): 在按钮点击 handler 里调 setMapProvider 后,
     * 用 setTimeout(0) 推到下一个 macrotask 再 init。click handler 运行在 macrotask 上下文,
     * 浏览器已经完成上一帧的 layout/paint; React commit 后再让出一拍 (setTimeout 0), 此时浏览器
     * 一定完成 display 切换的 layout, 容器有真实尺寸, new Map 万无一失。
     *
     * <p>额外防线:
     * <ul>
     *   <li>container.offsetWidth 主动读一下, 强制同步 layout (浏览器 lazy 计算时兜底);</li>
     *   <li>ResizeObserver 后续容器尺寸再变也自动 resize, 不会出现 "切走再切回变形" 的退化;</li>
     *   <li>map.on('error') 输出错误日志, token / style URL / tile CDN 问题第一时间看 console。</li>
     * </ul>
     */
    const initMapbox = useCallback(() => {
        if (mapboxInstanceRef.current) return;
        if (!mapboxContainerRef.current) return;
        const container = mapboxContainerRef.current;

        // 主动触发一次同步 layout 计算 - 防止 mapbox 内部读到陈旧的 0×0
        void container.offsetWidth;

        let map: mapboxgl.Map;
        try {
            map = new mapboxgl.Map({
                container,
                // 用 OSM 直连 raster tile, 绕开 *.mapbox.com (避免被广告拦截器全站拦掉导致白屏).
                // 类型断言: MAPBOX_OSM_STYLE 是 unknown, mapbox-gl 接受 Style 对象作为 style.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                style: MAPBOX_OSM_STYLE as any,
                center: [center.lng, center.lat],
                zoom: DEFAULT_DETAIL_ZOOM,
            });
        } catch (err) {
            // new Map() 抛异常 (token 错 / style 非法等) — 留 error 级日志便于排查, 不影响其他流程
            console.error('[AoiDetailMap] new mapboxgl.Map() 抛异常', err);
            return;
        }
        // 同步写 ref - 让 fitBounds / layer-update 等 effect 能立即拿到实例
        mapboxInstanceRef.current = map;

        const ro = new ResizeObserver(() => {
            mapboxInstanceRef.current?.resize();
        });
        ro.observe(container);
        mapboxResizeObserverRef.current = ro;

        // 网络问题 (CDN 被墙 / token 错 / style URL 404) 等 mapbox 内部错误统一走这里 console.error,
        // 用户白屏时贴 console 出来能定位; 正常路径不刷日志.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map.on('error', (e: any) => {
            const err = e?.error ?? e;
            console.error('[AoiDetailMap] mapbox error:', err?.message ?? err, err);
        });

        map.on('load', () => {
            map.resize();
            renderMapboxLayers(map, detail, effectiveEntryPoints, effectiveParkingPoints, layerVisibility, mapboxMarkersRef, highlightedAddressPoints, addressPointsDimmed, addressConnectors, selectedAddressId, {
                draggablePoints: editable && !pickTarget,
                onPointDrag,
                activePopupRef: mapboxActivePopupRef,
            });
            // 双保险 resize: 给 mapbox 一帧时间把 canvas 真正附加上, 再 resize 一次,
            // 防止首帧 tile 在 stale dimension 上算导致的轻微错位。
            requestAnimationFrame(() => mapboxInstanceRef.current?.resize());
        });
    }, [center.lng, center.lat, detail, effectiveEntryPoints, effectiveParkingPoints,
        layerVisibility, editable, pickTarget, onPointDrag]);

    /*
     * 兜底 useEffect - 处理 "首次挂载时 provider 已经是 mapbox" 的边缘情况
     * (e.g. URL 参数初始化 / 外部受控 provider 默认值变化), 走跟点击 handler 一样的 setTimeout(0) 模式。
     * 用户在页面里点 Google/Mapbox 切换走的是 handleSwitch 里的 setTimeout(0), 不进这条路径。
     */
    useEffect(() => {
        if (provider !== 'mapbox') return;
        if (mapboxInstanceRef.current) return;
        const timer = window.setTimeout(() => initMapbox(), 0);
        return () => window.clearTimeout(timer);
    }, [provider, initMapbox]);

    // 组件 unmount 时销毁 Mapbox 实例 (provider 切换不销毁)
    useEffect(() => {
        return () => {
            mapboxMarkersRef.current.forEach((m) => m.remove());
            mapboxMarkersRef.current = [];
            if (mapboxTempMarkerRef.current) {
                mapboxTempMarkerRef.current.remove();
                mapboxTempMarkerRef.current = null;
            }
            if (mapboxResizeObserverRef.current) {
                mapboxResizeObserverRef.current.disconnect();
                mapboxResizeObserverRef.current = null;
            }
            if (mapboxInstanceRef.current) {
                mapboxInstanceRef.current.remove();
                mapboxInstanceRef.current = null;
            }
        };
    }, []);

    // Mapbox 图层数据 / 可见性 同步 (editable / pickTarget / onPointDrag 变化也要重渲, 否则拖拽态会跟 UI 不一致)
    useEffect(() => {
        const map = mapboxInstanceRef.current;
        if (!map || !map.isStyleLoaded()) return;
        // 重渲 marker 前先关掉当前可能开着的 popup, 防止 marker 重建后 popup 指向已销毁的 element
        mapboxActivePopupRef.current?.remove();
        mapboxActivePopupRef.current = null;
        renderMapboxLayers(map, detail, effectiveEntryPoints, effectiveParkingPoints, layerVisibility, mapboxMarkersRef, highlightedAddressPoints, addressPointsDimmed, addressConnectors, selectedAddressId, {
            draggablePoints: editable && !pickTarget,
            onPointDrag,
            activePopupRef: mapboxActivePopupRef,
        });
    }, [detail, effectiveEntryPoints, effectiveParkingPoints, layerVisibility, editable, pickTarget, onPointDrag, highlightedAddressPoints, addressPointsDimmed, addressConnectors, selectedAddressId]);

    // 切到 mapbox 时调用 resize, 否则 hidden→visible 后会有渲染空白
    useEffect(() => {
        if (provider === 'mapbox' && mapboxInstanceRef.current) {
            setTimeout(() => mapboxInstanceRef.current?.resize(), 0);
        }
    }, [provider]);

    /* ====================== 聚焦当前 AOI ======================
     * 用 selfGeometry 的 bounding box 直接 fitBounds, 自动算最佳 center + zoom。
     * - 大 AOI: 自动缩小; 小 AOI: 自动放大, 但 cap 在 FIT_BOUNDS_MAX_ZOOM 防止缩到街道级看不清环境
     * - 切换不同 AOI (aoiId 变了) 时重新 fit
     * - **同一个 aoi 只 fit 一次** — 用 fittedKeyRef 记录 "provider:aoiId" 已 fit 过的, 避免 edit↔view 切换 /
     *   detail refetch (新对象引用) 触发反复 fit 产生伸缩动画
     */
    // useMemo 用具体经纬度作 deps 而不是 detail.selfGeometry 对象引用 - Apollo 缓存返回的对象引用容易换
    const selfMinLat = detail.selfGeometry?.minLatitude;
    const selfMinLng = detail.selfGeometry?.minLongitude;
    const selfMaxLat = detail.selfGeometry?.maxLatitude;
    const selfMaxLng = detail.selfGeometry?.maxLongitude;
    const selfBounds = useMemo(() => {
        if (selfMinLat == null || selfMinLng == null
            || selfMaxLat == null || selfMaxLng == null) return null;
        return { minLat: selfMinLat, minLng: selfMinLng, maxLat: selfMaxLat, maxLng: selfMaxLng };
    }, [selfMinLat, selfMinLng, selfMaxLat, selfMaxLng]);

    // 记录每个 provider 上已经 fit 过的 aoiId, 避免重复 fit 触发反复伸缩动画
    const fittedKeyRef = useRef<string | null>(null);

    // Google fitBounds (aoiId 变 / 地图加载完 / 切到 google 时触发; 同 aoi 不重 fit)
    // googleMapReady 是关键 dep: onLoad 触发它从 false → true, 这时 effect 才能拿到真正的 map 实例
    useEffect(() => {
        if (provider !== 'google') return;
        if (!googleMapReady) return;
        const map = googleMapRef.current;
        if (!map || !selfBounds) return;
        const key = `google:${detail.aoiId}`;
        if (fittedKeyRef.current === key) return;
        const bounds = new google.maps.LatLngBounds(
            { lat: selfBounds.minLat, lng: selfBounds.minLng },
            { lat: selfBounds.maxLat, lng: selfBounds.maxLng },
        );
        map.fitBounds(bounds, FIT_BOUNDS_PADDING_PX);
        fittedKeyRef.current = key;
        // cap maxZoom (fitBounds 不支持直接传 maxZoom, 用 idle 事件 once 拦截)
        const listener = google.maps.event.addListenerOnce(map, 'idle', () => {
            const z = map.getZoom();
            if (z != null && z > FIT_BOUNDS_MAX_ZOOM) map.setZoom(FIT_BOUNDS_MAX_ZOOM);
        });
        return () => google.maps.event.removeListener(listener);
    }, [provider, selfBounds, detail.aoiId, isLoaded, googleMapReady]);

    // Mapbox fitBounds (跟 Google 同样规则, 同 aoi 不重 fit)
    useEffect(() => {
        if (provider !== 'mapbox') return;
        const map = mapboxInstanceRef.current;
        if (!map || !selfBounds) return;
        const key = `mapbox:${detail.aoiId}`;
        if (fittedKeyRef.current === key) return;
        // 等 style 加载完才能 fitBounds, 否则 resize / projection 还没就绪
        const doFit = () => {
            map.fitBounds(
                [
                    [selfBounds.minLng, selfBounds.minLat],
                    [selfBounds.maxLng, selfBounds.maxLat],
                ],
                { padding: FIT_BOUNDS_PADDING_PX, maxZoom: FIT_BOUNDS_MAX_ZOOM, duration: 0 },
            );
            fittedKeyRef.current = key;
        };
        if (map.isStyleLoaded()) doFit();
        else map.once('load', doFit);
    }, [provider, selfBounds, detail.aoiId]);

    /* ====================== 拾取模式: Mapbox 点击 + 光标 ====================== */
    useEffect(() => {
        const map = mapboxInstanceRef.current;
        if (!map) return;
        // 上次绑定的 handler 先清掉, 避免重复触发
        if (mapboxClickHandlerRef.current) {
            map.off('click', mapboxClickHandlerRef.current);
            mapboxClickHandlerRef.current = null;
        }
        if (pickTarget && onPickClick) {
            const handler = (e: mapboxgl.MapMouseEvent) => {
                onPickClick(e.lngLat.lng, e.lngLat.lat);
            };
            map.on('click', handler);
            mapboxClickHandlerRef.current = handler;
            map.getCanvas().style.cursor = 'crosshair';
        } else {
            map.getCanvas().style.cursor = '';
        }
        return () => {
            if (mapboxClickHandlerRef.current && mapboxInstanceRef.current) {
                mapboxInstanceRef.current.off('click', mapboxClickHandlerRef.current);
                mapboxClickHandlerRef.current = null;
            }
        };
    }, [pickTarget, onPickClick]);

    /* ====================== 拾取模式: Mapbox 临时 marker (含拖拽微调) ====================== */
    useEffect(() => {
        const map = mapboxInstanceRef.current;
        if (!map) return;
        // 先清旧的
        if (mapboxTempMarkerRef.current) {
            mapboxTempMarkerRef.current.remove();
            mapboxTempMarkerRef.current = null;
        }
        if (pickTarget && tempPoint) {
            const color = pickTarget === 'entryPoints' ? AOI_DETAIL_COLORS.entryPoint : AOI_DETAIL_COLORS.parkingPoint;
            const el = document.createElement('div');
            el.style.cssText = `
                width: 22px;
                height: 22px;
                border-radius: 50%;
                background: ${color};
                opacity: 0.7;
                border: 3px dashed #ffffff;
                box-shadow: 0 2px 6px rgba(0,0,0,0.4);
                cursor: move;
            `;
            const marker = new mapboxgl.Marker({ element: el, draggable: true })
                .setLngLat([tempPoint.lng, tempPoint.lat])
                .addTo(map);
            marker.on('dragend', () => {
                const ll = marker.getLngLat();
                onPickClick?.(ll.lng, ll.lat);
            });
            mapboxTempMarkerRef.current = marker;
        }
    }, [pickTarget, tempPoint, onPickClick]);

    /* ====================== 切换按钮 (左下角) ======================
     *
     * <p>切到 Mapbox 时主动 init - 不依赖 useEffect 的时机 (那个时机太早, 浏览器还没完成 layout)。
     * setTimeout(0) 让 setMapProvider 触发的 React commit + 浏览器 layout 都跑完, 容器拿到真实尺寸,
     * 再 new Map → 跟 AddressLocationReviewDrawer 一致的稳定模式, 不会再 0×0 白屏。
     */
    const handleSwitch = useCallback((next: MapProvider) => {
        if (next === provider) return;
        onProviderChange(next);
        if (next === 'mapbox' && !mapboxInstanceRef.current) {
            window.setTimeout(() => initMapbox(), 0);
        }
    }, [provider, onProviderChange, initMapbox]);

    /* ====================== Google: 点击 + 拖动临时 marker ======================
     *
     * <p>两种行为分支:
     * <ul>
     *   <li>拾取模式 (pickTarget 非空): 把点击坐标传给父组件加进 draft;</li>
     *   <li>正常模式: 关掉当前打开的所有 popup, 跟 Mapbox closeOnClick:true / Apple Maps / Google
     *       原生 InfoWindow 的体验对齐 — 点地图空白处 = 弹框消失。
     *       注: 点 marker / popup 内部的事件不会冒泡到 GoogleMap 的 onClick (Google 已经吞了),
     *       所以这里直接清 state 不会误关刚点开的 popup。</li>
     * </ul>
     */
    const handleGoogleClick = useCallback((e: google.maps.MapMouseEvent) => {
        if (pickTarget && onPickClick && e.latLng) {
            onPickClick(e.latLng.lng(), e.latLng.lat());
            return;
        }
        // 非拾取模式: 点空白 → 关 popup
        setActiveAddressClusterKey(null);
        setActivePointKey(null);
    }, [pickTarget, onPickClick]);

    const handleGoogleTempDragEnd = useCallback((e: google.maps.MapMouseEvent) => {
        if (!onPickClick || !e.latLng) return;
        onPickClick(e.latLng.lng(), e.latLng.lat());
    }, [onPickClick]);

    return (
        /*
         * 用 absolute inset-0 撑满父级 box, 而不是 relative w-full h-full.
         *
         * <p><b>为啥</b>: AoiDetailMap 的父级是 `<div className="flex-1 relative">`, 它的高度由 flex
         * align-items:stretch 算出 (COMPUTED height), 但 CSS `height` 属性仍然是 auto. CSS percentage
         * (h-full = height: 100%) 读的是父级的 CSS height (= auto), 不是 computed height, 所以解不出来,
         * h-full 退化成 0. 整条 percentage 链路就断在这里, 导致内部 Mapbox 容器拿不到尺寸 → 白屏.
         *
         * <p>改用 absolute inset-0 走 "box positioning": 不读 CSS height percentage, 而是用
         * top/right/bottom/left 直接对齐父级的 padding box. 父级 box 是浏览器实际渲染的, 高度由 flex 拉伸,
         * 不管 CSS height 是 auto 还是 explicit, 我们都能撑满它. 父级 (`flex-1 relative`) 是 positioning
         * context, absolute 子元素自然以它为基准.
         *
         * <p>这里仍然需要 position 让内部按钮 / 拾取条 / popup 等 absolute 子元素能定位到自己, 改用 inline
         * position 防止 mapbox-gl.css 的 .mapboxgl-map { position: relative } 覆盖 (虽然这个 div 不挂
         * mapboxgl-map class, 但保持风格统一).
         */
        <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}>
            {/* Google 区块 */}
            <div className="absolute inset-0" style={{ display: provider === 'google' ? 'block' : 'none' }}>
                {loadError && <div className="p-4 text-red-500">Google Maps 加载失败</div>}
                {!isLoaded && !loadError && (
                    <div className="w-full h-full flex items-center justify-center"><Spin /></div>
                )}
                {isLoaded && (
                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        // center / zoom 是 mount 时的默认值; mount 后由 fitBounds effect 接管。
                        center={center}
                        zoom={DEFAULT_DETAIL_ZOOM}
                        // 关掉左上角 Map/Satellite 切换 + 右下角街景小人 (pegman); 当前页面用不到。
                        // draggableCursor: 拾取模式下变成十字光标, 增强 affordance。
                        options={{
                            mapTypeControl: false,
                            streetViewControl: false,
                            draggableCursor: pickTarget ? 'crosshair' : undefined,
                        }}
                        onLoad={(map) => { googleMapRef.current = map; setGoogleMapReady(true); }}
                        onUnmount={() => { googleMapRef.current = null; setGoogleMapReady(false); }}
                        onClick={handleGoogleClick}
                    >
                        {/* 周边 AOI - 灰蓝 (低饱和 + 低透明度 + 细描边, 多个堆叠也不会视觉过载) */}
                        {layerVisibility.showNearby && nearbyPolygonPathsList.map(({ aoiId, paths }) =>
                            paths.map((path, idx) => (
                                <Polygon
                                    key={`nb-${aoiId}-${idx}`}
                                    paths={path}
                                    options={{
                                        fillColor: AOI_DETAIL_COLORS.nearby,
                                        fillOpacity: 0.12,
                                        strokeColor: AOI_DETAIL_COLORS.nearby,
                                        strokeOpacity: 0.55,
                                        strokeWeight: 1,
                                        clickable: false,
                                    }}
                                />
                            )),
                        )}
                        {/* 子 AOI - 青色 */}
                        {layerVisibility.showChildren && childPolygonPathsList.map(({ aoiId, paths }) =>
                            paths.map((path, idx) => (
                                <Polygon
                                    key={`ch-${aoiId}-${idx}`}
                                    paths={path}
                                    options={{
                                        fillColor: AOI_DETAIL_COLORS.child,
                                        fillOpacity: 0.25,
                                        strokeColor: AOI_DETAIL_COLORS.child,
                                        strokeWeight: 2,
                                        // 拾取模式下 polygon 不要拦截点击, 否则点到子 AOI 上拿不到 latLng
                                        clickable: !pickTarget,
                                    }}
                                />
                            )),
                        )}
                        {/* 自身 - 紫色高亮 (描边用更深的紫加强轮廓, 填充更实, 视觉权重最高) */}
                        {selfPolygonPaths.map((path, idx) => (
                            <Polygon
                                key={`self-${idx}`}
                                paths={path}
                                options={{
                                    fillColor: AOI_DETAIL_COLORS.self,
                                    fillOpacity: 0.45,
                                    strokeColor: AOI_DETAIL_COLORS.selfStroke,
                                    strokeWeight: 4,
                                    zIndex: 100, // 压在周边 / 子 AOI 上面, 防止 z-fighting 看不清自己
                                    // 同上, 拾取模式下放行 click 给 map
                                    clickable: !pickTarget,
                                }}
                            />
                        ))}
                        {/* 进门点 */}
                        {layerVisibility.showEntryPoints && effectiveEntryPoints.map((p, idx) =>
                            renderGoogleMarker(p, idx, AOI_DETAIL_COLORS.entryPoint, `entry-${idx}`, {
                                draggable: editable && !pickTarget,
                                onDragEnd: onPointDrag ? (i, lng, lat) => onPointDrag('entryPoints', i, lng, lat) : undefined,
                                onClick: () => setActivePointKey(`entry-${idx}`),
                            }),
                        )}
                        {/* 停车点 */}
                        {layerVisibility.showParkingPoints && effectiveParkingPoints.map((p, idx) =>
                            renderGoogleMarker(p, idx, AOI_DETAIL_COLORS.parkingPoint, `park-${idx}`, {
                                draggable: editable && !pickTarget,
                                onDragEnd: onPointDrag ? (i, lng, lat) => onPointDrag('parkingPoints', i, lng, lat) : undefined,
                                onClick: () => setActivePointKey(`park-${idx}`),
                            }),
                        )}
                        {/* 地址级点位 → 所属地址 连线(虚线, 线画在点下面; 选中地址时其连线加粗高亮, 其余淡化) */}
                        {layerVisibility.showAddressPoints && addressConnectors.flatMap((conn) => {
                            const sel = selectedAddressId == null || selectedAddressId === conn.addressCoreId;
                            return conn.points.map((p, i) => {
                                const color = p.kind === 'entry'
                                    ? AOI_DETAIL_COLORS.entryPoint : AOI_DETAIL_COLORS.parkingPoint;
                                return (
                                    <Polyline
                                        key={`conn-${conn.addressCoreId}-${i}`}
                                        path={[{ lat: conn.addrLat, lng: conn.addrLng }, { lat: p.lat, lng: p.lng }]}
                                        options={{
                                            strokeColor: color,
                                            strokeOpacity: 0,   // 用 icons 画虚线, 底线透明
                                            zIndex: sel ? 850 : 800,
                                            icons: [{
                                                icon: {
                                                    path: 'M 0,-1 0,1',
                                                    strokeColor: color,
                                                    strokeOpacity: sel ? 0.9 : 0.25,
                                                    strokeWeight: sel ? 2.5 : 1.5,
                                                    scale: sel ? 3 : 2,
                                                },
                                                offset: '0',
                                                repeat: '10px',
                                            }],
                                        }}
                                    />
                                );
                            });
                        })}
                        {/* 地址级点位 (关联地址各自的进门/停车点, 空心圈样式区分楼级实心点; 选中某地址时其余淡化) */}
                        {layerVisibility.showAddressPoints && addressPointClusters.map((c) => (
                            <Marker
                                key={`addr-point-${c.key}`}
                                position={{ lat: c.lat, lng: c.lng }}
                                icon={{
                                    path: google.maps.SymbolPath.CIRCLE,
                                    fillColor: '#ffffff',
                                    fillOpacity: addressPointsDimmed ? 0.25 : 0.9,
                                    scale: 4.5,
                                    strokeColor: c.kind === 'entry'
                                        ? AOI_DETAIL_COLORS.entryPoint
                                        : AOI_DETAIL_COLORS.parkingPoint,
                                    strokeOpacity: addressPointsDimmed ? 0.3 : 1,
                                    strokeWeight: 2.5,
                                }}
                                onClick={() => setActiveAddressPointKey(c.key)}
                                title={`地址级${c.kind === 'entry' ? '进门点' : '停车点'} (${c.items.length} 个地址)`}
                            />
                        ))}
                        {/* 选中地址的高亮点位: 实心大点 + 光环, 压在聚合层之上 */}
                        {layerVisibility.showAddressPoints && highlightedAddressPoints.map((p, idx) => {
                            const color = p.kind === 'entry'
                                ? AOI_DETAIL_COLORS.entryPoint : AOI_DETAIL_COLORS.parkingPoint;
                            return (
                                <Marker
                                    key={`addr-hl-${idx}`}
                                    position={{ lat: p.lat, lng: p.lng }}
                                    zIndex={900}
                                    icon={{
                                        path: google.maps.SymbolPath.CIRCLE,
                                        fillColor: color,
                                        fillOpacity: 1,
                                        scale: 8,
                                        strokeColor: '#ffffff',
                                        strokeWeight: 3,
                                    }}
                                    title={`选中地址的${p.kind === 'entry' ? '进门点' : '停车点'}`}
                                />
                            );
                        })}
                        {/* 地址级点位 popup */}
                        {activeAddressPointCluster && (
                            <InfoWindow
                                position={{ lat: activeAddressPointCluster.lat, lng: activeAddressPointCluster.lng }}
                                onCloseClick={() => setActiveAddressPointKey(null)}
                                options={{ headerDisabled: true }}
                            >
                                <AddressPointPopupContent
                                    cluster={activeAddressPointCluster}
                                    onClose={() => setActiveAddressPointKey(null)}
                                />
                            </InfoWindow>
                        )}
                        {/* 关联地址 (按坐标聚合, 点击展开 popup) */}
                        {layerVisibility.showRelatedAddresses && addressClusters.map((c) => (
                            <Marker
                                key={`addr-cluster-${c.key}`}
                                position={{ lat: c.lat, lng: c.lng }}
                                icon={{
                                    path: google.maps.SymbolPath.CIRCLE,
                                    fillColor: AOI_DETAIL_COLORS.relatedAddress,
                                    fillOpacity: 1,
                                    scale: c.addresses.length > 1 ? 6 : 5,
                                    strokeColor: '#ffffff',
                                    strokeWeight: 1.5,
                                }}
                                onClick={() => setActiveAddressClusterKey(c.key)}
                                title={
                                    c.addresses.length > 1
                                        ? `${c.addresses.length} 条地址在此点`
                                        : c.addresses[0]?.fullStandardAddress ?? undefined
                                }
                            />
                        ))}
                        {/* 关联地址 popup (Google InfoWindow); headerDisabled 关掉默认 X 按钮腾出空间, 用自己的关闭按钮 */}
                        {activeAddressCluster && (
                            <InfoWindow
                                position={{ lat: activeAddressCluster.lat, lng: activeAddressCluster.lng }}
                                onCloseClick={() => setActiveAddressClusterKey(null)}
                                options={{ headerDisabled: true }}
                            >
                                <AddressClusterPopupContent
                                    cluster={activeAddressCluster}
                                    onClose={() => setActiveAddressClusterKey(null)}
                                />
                            </InfoWindow>
                        )}
                        {/* 进门 / 停车点 popup - 点击 marker 时弹出, 标识类型 + 经纬度 + 备注 */}
                        {activePoint && activePoint.point.lat != null && activePoint.point.lng != null && (
                            <InfoWindow
                                position={{ lat: activePoint.point.lat, lng: activePoint.point.lng }}
                                onCloseClick={() => setActivePointKey(null)}
                                options={{ headerDisabled: true }}
                            >
                                <PointPopupContent
                                    typeLabel={activePoint.type}
                                    color={activePoint.color}
                                    lng={activePoint.point.lng}
                                    lat={activePoint.point.lat}
                                    remark={activePoint.point.remark}
                                    onClose={() => setActivePointKey(null)}
                                />
                            </InfoWindow>
                        )}
                        {/* 临时拾取 marker (半透明大圆 + 虚线边, 可拖动微调) */}
                        {pickTarget && tempPoint && (
                            <Marker
                                position={{ lat: tempPoint.lat, lng: tempPoint.lng }}
                                draggable
                                onDragEnd={handleGoogleTempDragEnd}
                                icon={{
                                    path: google.maps.SymbolPath.CIRCLE,
                                    fillColor: pickTarget === 'entryPoints'
                                        ? AOI_DETAIL_COLORS.entryPoint
                                        : AOI_DETAIL_COLORS.parkingPoint,
                                    fillOpacity: 0.7,
                                    scale: 11,
                                    strokeColor: '#ffffff',
                                    strokeWeight: 3,
                                }}
                                zIndex={1000}
                            />
                        )}
                    </GoogleMap>
                )}
            </div>

            {/*
             * Mapbox 区块 (容器始终挂载, 通过 display 切换).
             *
             * <p><b>关键: position 用 inline style 写</b>:
             * Mapbox SDK 实例化时会给容器加 `mapboxgl-map` class, 这个 class 在 mapbox-gl.css 里
             * 定义了 `position: relative; overflow: hidden`. 如果我们用 Tailwind 的 `.absolute` class
             * (跟 mapbox-gl.css 同样 class-selector 优先级), mapbox-gl.css 后加载就盖掉我们的, 容器
             * 变成 position:relative, 之后 inset:0 完全失效 → 容器退化成 0 高度 → 白屏.
             *
             * <p>解决: 用 <b>inline style</b> 写 position - inline 比 class selector 优先级高,
             * Mapbox 的 .mapboxgl-map 怎么改 class 都盖不掉 inline 上的 absolute. 然后 top/bottom 撑满
             * 父级 box (走绝对定位的 box-positioning, 不依赖 CSS height 百分比), 这样不管父级 height 是
             * 显式 / 隐式 / flex-stretched, 我们都能撑满它.
             */}
            <div
                ref={mapboxContainerRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    display: provider === 'mapbox' ? 'block' : 'none',
                }}
            />

            {/* 左下角底图切换按钮 */}
            <div className="absolute" style={{ bottom: 16, left: 16, zIndex: 1000 }}>
                <div className="bg-white rounded-md shadow-md flex overflow-hidden">
                    <button
                        type="button"
                        className="px-3 py-1.5 text-sm"
                        style={{
                            background: provider === 'google' ? AOI_DETAIL_COLORS.self : '#ffffff',
                            color: provider === 'google' ? '#fff' : '#374151',
                        }}
                        onClick={() => handleSwitch('google')}
                    >Google</button>
                    <button
                        type="button"
                        className="px-3 py-1.5 text-sm border-l border-gray-200"
                        style={{
                            background: provider === 'mapbox' ? AOI_DETAIL_COLORS.self : '#ffffff',
                            color: provider === 'mapbox' ? '#fff' : '#374151',
                        }}
                        onClick={() => handleSwitch('mapbox')}
                    >Mapbox</button>
                </div>
            </div>
        </div>
    );
};

/* ====================== 辅助函数 ====================== */

/** GeoJSON 字符串 → Google Maps Polygon paths (一个 polygon 可能多个 path = 外环+内环, 这里只取外环)。 */
function geomToGooglePaths(geom: BizAoiAttributeGeometry | null): google.maps.LatLngLiteral[][][] {
    if (!geom?.geometryGeoJson) return [];
    try {
        const obj = JSON.parse(geom.geometryGeoJson);
        return parseGeoJsonToPaths(obj);
    } catch {
        return [];
    }
}

function childAoiToGooglePaths(c: BizAoiAttributeChildAoi): google.maps.LatLngLiteral[][][] {
    if (!c.geometryGeoJson) return [];
    try {
        return parseGeoJsonToPaths(JSON.parse(c.geometryGeoJson));
    } catch {
        return [];
    }
}

/**
 * 解析 GeoJSON 多边形 → Google 嵌套 paths 数组。
 *
 * <p>返回结构: outermost[] = 每个 Polygon 一组, 每组 paths[] = 外环 + 内环数组。
 * Google 的 <Polygon paths={...}> 接受 LatLngLiteral[] 或 LatLngLiteral[][] (含内环 hole)。
 */
function parseGeoJsonToPaths(obj: { type: string; coordinates: number[][] | number[][][] | number[][][][] }): google.maps.LatLngLiteral[][][] {
    if (obj.type === 'Polygon') {
        const coords = obj.coordinates as number[][][];
        return [coords.map((ring) => ring.map(([lng, lat]) => ({ lat, lng })))];
    }
    if (obj.type === 'MultiPolygon') {
        const coords = obj.coordinates as number[][][][];
        return coords.map((poly) => poly.map((ring) => ring.map(([lng, lat]) => ({ lat, lng }))));
    }
    return [];
}

function renderGoogleMarker(
    point: BizAoiAttributeLocationPoint,
    idx: number,
    color: string,
    key: string,
    options?: {
        draggable?: boolean;
        onDragEnd?: (idx: number, lng: number, lat: number) => void;
        /** 点击 marker 时回调 (展开 popup), 跟 draggable 互斥 — drag 模式下不开 popup, 避免误触。 */
        onClick?: () => void;
    },
): React.ReactNode {
    if (point.lat == null || point.lng == null) return null;
    return (
        <Marker
            key={key}
            position={{ lat: point.lat, lng: point.lng }}
            icon={{
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: color,
                fillOpacity: 1,
                scale: 7,
                strokeColor: '#ffffff',
                strokeWeight: 2,
            }}
            title={point.remark ?? undefined}
            draggable={options?.draggable}
            onDragEnd={options?.draggable ? (e) => {
                if (!e.latLng || !options.onDragEnd) return;
                options.onDragEnd(idx, e.latLng.lng(), e.latLng.lat());
            } : undefined}
            onClick={!options?.draggable && options?.onClick ? options.onClick : undefined}
        />
    );
}

/**
 * Mapbox 图层渲染 (命令式): 清除旧的 source / layer / marker, 重新加。
 * 每次 detail / visibility / points 变化触发, 简单粗暴但代价低 (单 AOI 详情数据量小)。
 *
 * <p>进门 / 停车点单独传 effectiveXxx 参数, 不直接读 detail.attribute, 这样
 * edit 模式可以传 draft 让地图实时反映用户改动。
 */
function renderMapboxLayers(
    map: mapboxgl.Map,
    detail: BizAoiAttributeDetail,
    effectiveEntryPoints: BizAoiAttributeLocationPoint[],
    effectiveParkingPoints: BizAoiAttributeLocationPoint[],
    visibility: LayerVisibility,
    markersRef: React.MutableRefObject<mapboxgl.Marker[]>,
    highlightPoints: HighlightPoint[],
    addressPointsDimmed: boolean,
    addressConnectors: AddrConnector[],
    selectedAddressId: number | null,
    options?: {
        /** 是否让进门 / 停车点 marker 可拖动 (edit 模式 + 非拾取中 = true) */
        draggablePoints?: boolean;
        /** 拖动结束后回调, 父组件用来更新 draft */
        onPointDrag?: (target: 'entryPoints' | 'parkingPoints', idx: number, lng: number, lat: number) => void;
        /** 全局唯一 popup ref - marker 点击时关旧开新, 保证整张地图同时只有一个 popup */
        activePopupRef?: React.MutableRefObject<mapboxgl.Popup | null>;
    },
) {
    // 清除旧 markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    // 清除旧 polygon 图层 (按 id 前缀清理)
    const allIds = ['self', 'children', 'nearby', 'addr-connectors'];
    allIds.forEach((id) => {
        if (map.getLayer(`${id}-fill`)) map.removeLayer(`${id}-fill`);
        if (map.getLayer(`${id}-line`)) map.removeLayer(`${id}-line`);
        if (map.getSource(id)) map.removeSource(id);
    });

    // 周边 (灰蓝, 低饱和 + 低透明度 + 细描边, 多个堆叠也不会视觉过载)
    if (visibility.showNearby && detail.nearbyGeometries.length > 0) {
        addMapboxPolygonLayer(map, 'nearby',
            detail.nearbyGeometries.map((g) => g.geometryGeoJson).filter(Boolean) as string[],
            AOI_DETAIL_COLORS.nearby, 0.12, 1);
    }
    // 子 AOI (青色)
    if (visibility.showChildren && detail.childAois.length > 0) {
        addMapboxPolygonLayer(map, 'children',
            detail.childAois.map((c) => c.geometryGeoJson).filter(Boolean) as string[],
            AOI_DETAIL_COLORS.child, 0.25, 2);
    }
    // 自身 (紫色高亮 - 描边用更深的紫加强轮廓, 填充更实)
    if (detail.selfGeometry?.geometryGeoJson) {
        addMapboxPolygonLayer(map, 'self', [detail.selfGeometry.geometryGeoJson],
            AOI_DETAIL_COLORS.self, 0.45, 4, AOI_DETAIL_COLORS.selfStroke);
    }

    // Markers (edit 模式下可拖动, 拖完通过 options.onPointDrag 回写到 draft;
    // 非 draggable 时挂 popup, 点 marker 弹 "进门/停车 + 经纬度 + 备注")
    const activePopupRef = options?.activePopupRef;
    if (visibility.showEntryPoints) {
        effectiveEntryPoints.forEach((p, idx) =>
            addMapboxPointMarker(map, p, AOI_DETAIL_COLORS.entryPoint, markersRef, {
                draggable: options?.draggablePoints,
                onDragEnd: options?.onPointDrag
                    ? (lng, lat) => options.onPointDrag!('entryPoints', idx, lng, lat)
                    : undefined,
                popupHtml: (p.lat != null && p.lng != null)
                    ? buildPointPopupHtml('进门点', AOI_DETAIL_COLORS.entryPoint, p.lng, p.lat, p.remark)
                    : undefined,
                activePopupRef,
            }));
    }
    if (visibility.showParkingPoints) {
        effectiveParkingPoints.forEach((p, idx) =>
            addMapboxPointMarker(map, p, AOI_DETAIL_COLORS.parkingPoint, markersRef, {
                draggable: options?.draggablePoints,
                onDragEnd: options?.onPointDrag
                    ? (lng, lat) => options.onPointDrag!('parkingPoints', idx, lng, lat)
                    : undefined,
                popupHtml: (p.lat != null && p.lng != null)
                    ? buildPointPopupHtml('停车点', AOI_DETAIL_COLORS.parkingPoint, p.lng, p.lat, p.remark)
                    : undefined,
                activePopupRef,
            }));
    }
    if (visibility.showRelatedAddresses && activePopupRef) {
        for (const cluster of clusterAddressesByCoord(detail.relatedAddresses)) {
            addMapboxAddressClusterMarker(map, cluster, markersRef, activePopupRef);
        }
    }
    // 地址级点位 → 所属地址 连线(虚线; 选中地址加粗高亮, 其余淡化)
    if (visibility.showAddressPoints && addressConnectors.length > 0) {
        const features = addressConnectors.flatMap((conn) => {
            const sel = selectedAddressId == null || selectedAddressId === conn.addressCoreId;
            return conn.points.map((p) => ({
                type: 'Feature' as const,
                geometry: {
                    type: 'LineString' as const,
                    coordinates: [[conn.addrLng, conn.addrLat], [p.lng, p.lat]],
                },
                properties: {
                    color: p.kind === 'entry' ? AOI_DETAIL_COLORS.entryPoint : AOI_DETAIL_COLORS.parkingPoint,
                    sel: sel ? 1 : 0,
                },
            }));
        });
        if (features.length) {
            map.addSource('addr-connectors', { type: 'geojson', data: { type: 'FeatureCollection', features } });
            map.addLayer({
                id: 'addr-connectors-line',
                type: 'line',
                source: 'addr-connectors',
                paint: {
                    'line-color': ['get', 'color'],
                    'line-dasharray': [2, 2],
                    'line-opacity': ['case', ['==', ['get', 'sel'], 1], 0.9, 0.25],
                    'line-width': ['case', ['==', ['get', 'sel'], 1], 2.5, 1.2],
                },
            });
        }
    }
    // 地址级点位 (空心圈样式区分楼级实心点; 选中某地址时其余淡化)
    if (visibility.showAddressPoints && activePopupRef) {
        for (const cluster of clusterAddressPointsByCoord(detail.relatedAddresses)) {
            addMapboxAddressPointMarker(map, cluster, markersRef, activePopupRef, addressPointsDimmed);
        }
        // 选中地址的高亮点位: 实心大点 + 白描边
        for (const hp of highlightPoints) {
            const color = hp.kind === 'entry' ? AOI_DETAIL_COLORS.entryPoint : AOI_DETAIL_COLORS.parkingPoint;
            const el = document.createElement('div');
            el.style.cssText = `width:18px;height:18px;border-radius:50%;box-sizing:border-box;`
                + `background:${color};border:3px solid #fff;box-shadow:0 0 0 2px ${color}55,0 1px 4px rgba(0,0,0,0.4);`;
            el.title = `选中地址的${hp.kind === 'entry' ? '进门点' : '停车点'}`;
            markersRef.current.push(new mapboxgl.Marker(el).setLngLat([hp.lng, hp.lat]).addTo(map));
        }
    }
}

/**
 * Mapbox 多边形图层渲染。
 *
 * @param color       填充色 (描边色默认同色)
 * @param fillOpacity 填充透明度
 * @param lineWidth   描边线宽
 * @param strokeColor 可选, 单独指定描边色 (例如 self 用更深的紫色描边强化轮廓)
 */
function addMapboxPolygonLayer(
    map: mapboxgl.Map,
    sourceId: string,
    geoJsonStrings: string[],
    color: string,
    fillOpacity: number,
    lineWidth: number,
    strokeColor?: string,
) {
    const features = geoJsonStrings.flatMap((s) => {
        try {
            const geom = JSON.parse(s);
            return [{ type: 'Feature' as const, geometry: geom, properties: {} }];
        } catch {
            return [];
        }
    });
    if (features.length === 0) return;
    map.addSource(sourceId, { type: 'geojson', data: { type: 'FeatureCollection', features } });
    map.addLayer({
        id: `${sourceId}-fill`,
        type: 'fill',
        source: sourceId,
        paint: { 'fill-color': color, 'fill-opacity': fillOpacity },
    });
    map.addLayer({
        id: `${sourceId}-line`,
        type: 'line',
        source: sourceId,
        paint: { 'line-color': strokeColor ?? color, 'line-width': lineWidth },
    });
}

function addMapboxPointMarker(
    map: mapboxgl.Map,
    point: BizAoiAttributeLocationPoint,
    color: string,
    markersRef: React.MutableRefObject<mapboxgl.Marker[]>,
    options?: {
        draggable?: boolean;
        onDragEnd?: (lng: number, lat: number) => void;
        /** 点击 marker 时弹的 popup HTML, 不传则不弹 popup; draggable 模式下应该不传 (优先 drag) */
        popupHtml?: string;
        /**
         * 全局唯一 popup 引用 - 点新 marker 时先关旧 popup, 保证同时只有一个开着。
         * 跟世界级地图 popup 行为对齐 (Google InfoWindow / Apple Maps / 等)。
         */
        activePopupRef?: React.MutableRefObject<mapboxgl.Popup | null>;
    },
) {
    if (point.lat == null || point.lng == null) return;
    const el = document.createElement('div');
    const cursor = options?.draggable ? 'cursor:move;' : (options?.popupHtml ? 'cursor:pointer;' : '');
    el.style.cssText = `width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.3);${cursor}`;
    if (point.remark) el.title = point.remark;
    const marker = new mapboxgl.Marker({ element: el, draggable: !!options?.draggable })
        .setLngLat([point.lng, point.lat])
        .addTo(map);
    if (options?.draggable && options.onDragEnd) {
        marker.on('dragend', () => {
            const ll = marker.getLngLat();
            options.onDragEnd!(ll.lng, ll.lat);
        });
    }
    // 非拖拽态时挂 popup; 点 marker 时手动管理 (关旧的 → 开新的), 不用默认 togglePopup, 保证全局只有一个 popup 开着
    if (!options?.draggable && options?.popupHtml && options.activePopupRef) {
        const activePopupRef = options.activePopupRef;
        const popupHtml = options.popupHtml;
        el.addEventListener('click', (e) => {
            e.stopPropagation();
            // 如果当前 marker 的 popup 已经开着, 再点 = 关闭 (toggle)
            if (activePopupRef.current && activePopupRef.current.getElement()?.dataset.markerKey === marker.getElement().dataset.markerKey) {
                activePopupRef.current.remove();
                activePopupRef.current = null;
                return;
            }
            // 先关旧的
            activePopupRef.current?.remove();
            // 创建新的; closeButton:false 是因为我们已经在 popupHtml 里自带了 X (跟 Google 一致)
            const popup = new mapboxgl.Popup({
                offset: 14,
                closeButton: false,
                closeOnClick: true, // 点地图任意空白处关闭 (mapbox 默认就是 true, 显式写一下)
                maxWidth: 'none',   // 取消默认 240px 限制, 让 popupHtml 自己定宽度
                className: 'aoi-popup', // 配合上面注入的 style 干掉默认背景 / 边框
            }).setHTML(popupHtml).setLngLat(marker.getLngLat()).addTo(map);
            // 给 popup element 打标记, 方便 toggle 时识别是不是同一个 marker 的 popup
            const popupEl = popup.getElement();
            if (popupEl) popupEl.dataset.markerKey = marker.getElement().dataset.markerKey ?? '';
            // X 关闭按钮 click → popup.remove()
            wirePopupCloseButton(popup);
            activePopupRef.current = popup;
            // 用户主动关闭 (X / 点地图空白处) 时, 同步 ref
            popup.on('close', () => {
                if (activePopupRef.current === popup) activePopupRef.current = null;
            });
        });
        // 给 marker element 打标记用于 toggle 比对
        marker.getElement().dataset.markerKey = `mk-${markersRef.current.length}`;
    }
    markersRef.current.push(marker);
}

/* ====================== 关联地址聚合 + popup ====================== */

/** popup 里最多列出几条地址, 超过的折叠成 "还有 N 个"。 */
const ADDRESS_POPUP_PREVIEW_LIMIT = 3;

/** 同一坐标的多条地址聚合成一个 cluster (key 用坐标字符串去重)。 */
interface AddressCluster {
    /** "lat,lng" 7 位精度, 用作 React key 和 React state 标识 */
    key: string;
    lat: number;
    lng: number;
    addresses: BizAoiAttributeRelatedAddress[];
}

/**
 * 按 (lat, lng) 聚合关联地址列表。
 *
 * <p>一栋楼可能有几百个 unit 共享同一坐标 (例如 ASU 自然历史博物馆 374 个 unit 都在同一中心点),
 * 渲染几百个 marker 既慢又叠在一起看不出区别。这里按坐标聚合后, 同坐标的多条地址走一个 marker,
 * 点击 marker 弹 popup 展开明细 (前 N 条 + 「还有 N 个」缩略)。
 *
 * <p>坐标 key 取 7 位小数 (≈ 1cm 精度), 防止浮点漂移导致同实际位置被拆成多个 cluster。
 * lat / lng 缺失的地址直接丢掉 (不能渲染)。
 */
function clusterAddressesByCoord(addresses: BizAoiAttributeRelatedAddress[]): AddressCluster[] {
    const map = new Map<string, AddressCluster>();
    for (const a of addresses) {
        if (a.latitude == null || a.longitude == null) continue;
        const key = `${a.latitude.toFixed(7)},${a.longitude.toFixed(7)}`;
        let bucket = map.get(key);
        if (!bucket) {
            bucket = { key, lat: a.latitude, lng: a.longitude, addresses: [] };
            map.set(key, bucket);
        }
        bucket.addresses.push(a);
    }
    return Array.from(map.values());
}

/* ====================== 地址级点位聚合 + popup ====================== */

/** "按地址查看"高亮点位: 选中地址自己的单个进门/停车点。 */
interface HighlightPoint {
    kind: 'entry' | 'park';
    lng: number;
    lat: number;
    remark: string | null;
}

/** 地址级点位 → 所属地址的连线数据: 一个地址 + 它自己的点位集合。 */
interface AddrConnector {
    addressCoreId: number;
    addrLng: number;
    addrLat: number;
    points: { kind: 'entry' | 'park'; lng: number; lat: number }[];
}

/** 地址级点位 cluster: 同 (类型, 坐标) 的多个地址点位聚合成一个空心圈 marker。 */
interface AddressPointCluster {
    /** "kind:lat,lng" 7 位精度, 用作 React key / popup toggle 标识 */
    key: string;
    kind: 'entry' | 'park';
    lat: number;
    lng: number;
    /** 这个坐标点归属的地址 + 各自备注 */
    items: { address: string | null; remark: string | null }[];
}

/**
 * 把关联地址各自的地址级进门/停车点按 (类型, 坐标) 聚合。
 * 多个 unit 地址的点位经常落在同一坐标 (同步时同一实采点挂到多条地址), 聚合后 popup 列明归属。
 */
function clusterAddressPointsByCoord(addresses: BizAoiAttributeRelatedAddress[]): AddressPointCluster[] {
    const map = new Map<string, AddressPointCluster>();
    const collect = (a: BizAoiAttributeRelatedAddress, points: BizAoiAttributeLocationPoint[] | undefined, kind: 'entry' | 'park') => {
        for (const p of points ?? []) {
            if (p.lat == null || p.lng == null) continue;
            const key = `${kind}:${p.lat.toFixed(7)},${p.lng.toFixed(7)}`;
            let bucket = map.get(key);
            if (!bucket) {
                bucket = { key, kind, lat: p.lat, lng: p.lng, items: [] };
                map.set(key, bucket);
            }
            bucket.items.push({ address: a.fullStandardAddress ?? `#${a.addressCoreId}`, remark: p.remark });
        }
    };
    for (const a of addresses) {
        collect(a, a.entryPoints, 'entry');
        collect(a, a.parkingPoints, 'park');
    }
    return Array.from(map.values());
}

/** 地址级点位 popup - Google InfoWindow 用; Mapbox 用 {@link buildAddressPointPopupHtml}。 */
const AddressPointPopupContent: React.FC<{
    cluster: AddressPointCluster;
    onClose: () => void;
}> = ({ cluster, onClose }) => {
    const color = cluster.kind === 'entry' ? AOI_DETAIL_COLORS.entryPoint : AOI_DETAIL_COLORS.parkingPoint;
    const visible = cluster.items.slice(0, ADDRESS_POPUP_PREVIEW_LIMIT);
    const overflow = cluster.items.length - visible.length;
    return (
        <div style={{ maxWidth: 360, fontSize: 13, lineHeight: '20px', color: '#1f2937' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                {/* 空心圈图标呼应地图 marker 样式 */}
                <span style={{
                    width: 10, height: 10, borderRadius: '50%',
                    background: '#fff', border: `2.5px solid ${color}`, flexShrink: 0,
                }} />
                <span style={{ flex: 1, fontWeight: 600, color: '#111827' }}>
                    地址级{cluster.kind === 'entry' ? '进门点' : '停车点'}
                </span>
                <PopupCloseButton onClose={onClose} />
            </div>
            <div style={{ color: '#374151', fontFamily: 'ui-monospace, monospace', fontSize: 12, marginBottom: 6 }}>
                {cluster.lat.toFixed(7)}, {cluster.lng.toFixed(7)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {visible.map((it, idx) => (
                    <div key={idx} style={{ color: '#374151' }}>
                        {it.address}
                        {it.remark && <span style={{ color: '#6b7280', fontSize: 12 }}>（{it.remark}）</span>}
                    </div>
                ))}
            </div>
            {overflow > 0 && (
                <div style={{
                    marginTop: 8, paddingTop: 8,
                    borderTop: '1px solid #e5e7eb',
                    color: '#6b7280', fontSize: 12,
                }}>
                    还有 {overflow} 个地址共用此点
                </div>
            )}
        </div>
    );
};

/** Mapbox popup HTML 版本 (跟 {@link AddressPointPopupContent} 保持视觉一致)。 */
function buildAddressPointPopupHtml(cluster: AddressPointCluster): string {
    const color = cluster.kind === 'entry' ? AOI_DETAIL_COLORS.entryPoint : AOI_DETAIL_COLORS.parkingPoint;
    const visible = cluster.items.slice(0, ADDRESS_POPUP_PREVIEW_LIMIT);
    const overflow = cluster.items.length - visible.length;
    const lines = visible
        .map((it) => {
            const remarkHtml = it.remark
                ? `<span style="color:#6b7280;font-size:12px">（${escapeHtml(it.remark)}）</span>`
                : '';
            return `<div style="color:#374151;white-space:nowrap">${escapeHtml(it.address ?? '-')}${remarkHtml}</div>`;
        })
        .join('');
    const overflowHtml = overflow > 0
        ? `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #e5e7eb;color:#6b7280;font-size:12px">还有 ${overflow} 个地址共用此点</div>`
        : '';
    return `
        <div style="font-size:13px;line-height:20px;color:#1f2937">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
                <span style="width:10px;height:10px;border-radius:50%;background:#fff;border:2.5px solid ${color};flex-shrink:0;box-sizing:border-box"></span>
                <span style="font-weight:600;color:#111827;flex:1">地址级${cluster.kind === 'entry' ? '进门点' : '停车点'}</span>
                <button type="button" class="aoi-popup-close" aria-label="关闭">×</button>
            </div>
            <div style="color:#374151;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12px;margin-bottom:5px">
                ${cluster.lat.toFixed(7)}, ${cluster.lng.toFixed(7)}
            </div>
            <div style="display:flex;flex-direction:column;gap:5px">${lines}</div>
            ${overflowHtml}
        </div>
    `;
}

/**
 * Mapbox 地址级点位 marker - 空心圈样式 (白底 + 类型色描边), 与楼级实心点视觉区分;
 * popup 管理复用全局唯一 activePopupRef 模式。
 */
function addMapboxAddressPointMarker(
    map: mapboxgl.Map,
    cluster: AddressPointCluster,
    markersRef: React.MutableRefObject<mapboxgl.Marker[]>,
    activePopupRef: React.MutableRefObject<mapboxgl.Popup | null>,
    dimmed: boolean = false,
) {
    const color = cluster.kind === 'entry' ? AOI_DETAIL_COLORS.entryPoint : AOI_DETAIL_COLORS.parkingPoint;
    const el = document.createElement('div');
    el.style.cssText = `
        width:13px;height:13px;border-radius:50%;box-sizing:border-box;
        background:#fff;border:3px solid ${color};
        box-shadow:0 1px 3px rgba(0,0,0,0.3);cursor:pointer;
        opacity:${dimmed ? 0.3 : 1};
    `;
    el.title = `地址级${cluster.kind === 'entry' ? '进门点' : '停车点'} (${cluster.items.length} 个地址)`;

    const marker = new mapboxgl.Marker(el)
        .setLngLat([cluster.lng, cluster.lat])
        .addTo(map);
    el.dataset.markerKey = `addr-point-${cluster.key}`;

    el.addEventListener('click', (e) => {
        e.stopPropagation();
        const curKey = activePopupRef.current?.getElement()?.dataset.markerKey;
        if (curKey === el.dataset.markerKey) {
            activePopupRef.current?.remove();
            activePopupRef.current = null;
            return;
        }
        activePopupRef.current?.remove();
        const popup = new mapboxgl.Popup({
            offset: 14,
            closeButton: false,
            closeOnClick: true,
            maxWidth: 'none',
            className: 'aoi-popup',
        }).setHTML(buildAddressPointPopupHtml(cluster)).setLngLat([cluster.lng, cluster.lat]).addTo(map);
        const popupEl = popup.getElement();
        if (popupEl) popupEl.dataset.markerKey = el.dataset.markerKey ?? '';
        wirePopupCloseButton(popup);
        activePopupRef.current = popup;
        popup.on('close', () => {
            if (activePopupRef.current === popup) activePopupRef.current = null;
        });
    });
    markersRef.current.push(marker);
}

/**
 * 关联地址 popup 内容 - Google InfoWindow 跟 Mapbox Popup 都用这块 HTML。
 *
 * <p>布局: 顶部坐标, 下面前 N 条地址, 超出 N 条显示 "还有 X 个" 缩略。
 * Mapbox 的 Popup 接受 HTML 字符串而不是 React 节点, 所以同时提供一个纯字符串版本 {@link buildAddressClusterPopupHtml}。
 */
const AddressClusterPopupContent: React.FC<{
    cluster: AddressCluster;
    onClose: () => void;
}> = ({ cluster, onClose }) => {
    const visible = cluster.addresses.slice(0, ADDRESS_POPUP_PREVIEW_LIMIT);
    const overflow = cluster.addresses.length - visible.length;
    return (
        <div style={{ maxWidth: 360, fontSize: 13, lineHeight: '20px', color: '#1f2937' }}>
            {/* Header: 红色定位图标圆点 + 坐标 + 右上自定义 X */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: AOI_DETAIL_COLORS.relatedAddress,
                    flexShrink: 0,
                }} />
                <span style={{ flex: 1, fontWeight: 600, color: '#111827', fontFamily: 'ui-monospace, monospace' }}>
                    {cluster.lat.toFixed(7)}, {cluster.lng.toFixed(7)}
                </span>
                <PopupCloseButton onClose={onClose} />
            </div>
            {/* Body: 地址列表 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {visible.map((a) => (
                    <div key={a.addressCoreId} style={{ color: '#374151' }}>
                        {a.fullStandardAddress ?? `#${a.addressCoreId}`}
                    </div>
                ))}
            </div>
            {/* Footer: 还有 N 个 */}
            {overflow > 0 && (
                <div style={{
                    marginTop: 8, paddingTop: 8,
                    borderTop: '1px solid #e5e7eb',
                    color: '#6b7280', fontSize: 12,
                }}>
                    还有 {overflow} 个地址, 完整列表请到右侧面板查看
                </div>
            )}
        </div>
    );
};

/** Popup 通用关闭按钮 - 一个 X icon, 内联在 header 行右侧, 不占独立一行 */
const PopupCloseButton: React.FC<{ onClose: () => void }> = ({ onClose }) => (
    <button
        type="button"
        onClick={onClose}
        title="关闭"
        style={{
            border: 'none', background: 'transparent', cursor: 'pointer',
            color: '#9ca3af', fontSize: 16, lineHeight: 1, padding: 0,
            flexShrink: 0,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = '#374151'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; }}
    >×</button>
);

/**
 * 进门 / 停车点 popup 内容 - Google InfoWindow 用; Mapbox 用 {@link buildPointPopupHtml}。
 * 标识点位类型 (进门 / 停车) + 经纬度 + 可选备注; 自带紧凑的关闭按钮。
 */
const PointPopupContent: React.FC<{
    typeLabel: string;
    color: string;
    lng: number;
    lat: number;
    remark: string | null;
    onClose: () => void;
}> = ({ typeLabel, color, lng, lat, remark, onClose }) => (
    <div style={{ minWidth: 220, fontSize: 13, lineHeight: '20px', color: '#1f2937' }}>
        {/* Header: 类型彩色圆点 + 类型名 + 自定义 X 关闭 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{
                width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0,
            }} />
            <span style={{ flex: 1, fontWeight: 600, color: '#111827' }}>{typeLabel}</span>
            <PopupCloseButton onClose={onClose} />
        </div>
        {/* Body: 经纬度 (等宽字体) */}
        <div style={{ color: '#374151', fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
            <div>经度: {lng.toFixed(7)}</div>
            <div>纬度: {lat.toFixed(7)}</div>
        </div>
        {remark && (
            <div style={{
                marginTop: 8, paddingTop: 8,
                borderTop: '1px solid #e5e7eb',
                color: '#6b7280', fontSize: 12,
            }}>备注: {remark}</div>
        )}
    </div>
);

/** Mapbox popup HTML 版本 (跟 PointPopupContent 保持视觉一致)。 */
function buildPointPopupHtml(typeLabel: string, color: string, lng: number, lat: number, remark: string | null): string {
    const remarkHtml = remark
        ? `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #e5e7eb;color:#6b7280;font-size:12px;word-break:break-word">备注: ${escapeHtml(remark)}</div>`
        : '';
    // 经纬度合并一行 (lat, lng 用等宽字体, 数字对齐); 整体白底, 自带 X 关闭按钮 (依赖 .aoi-popup-close CSS class)
    return `
        <div style="font-size:13px;line-height:20px;color:#1f2937;white-space:nowrap">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
                <span style="width:10px;height:10px;border-radius:50%;background:${color};flex-shrink:0"></span>
                <span style="font-weight:600;color:#111827;flex:1">${escapeHtml(typeLabel)}</span>
                <button type="button" class="aoi-popup-close" aria-label="关闭">×</button>
            </div>
            <div style="color:#374151;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12px">
                ${lat.toFixed(7)}, ${lng.toFixed(7)}
            </div>
            ${remarkHtml}
        </div>
    `;
}

/** Mapbox Popup 需要 HTML 字符串, 跟 {@link AddressClusterPopupContent} 保持视觉一致; 自带 X 关闭按钮。 */
function buildAddressClusterPopupHtml(cluster: AddressCluster): string {
    const visible = cluster.addresses.slice(0, ADDRESS_POPUP_PREVIEW_LIMIT);
    const overflow = cluster.addresses.length - visible.length;
    // 单条地址不换行 (white-space:nowrap); cluster popup 整体允许换行因为地址可能比较长
    const lines = visible
        .map((a) => `<div style="color:#374151;white-space:nowrap">${escapeHtml(a.fullStandardAddress ?? `#${a.addressCoreId}`)}</div>`)
        .join('');
    const overflowHtml = overflow > 0
        ? `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #e5e7eb;color:#6b7280;font-size:12px">还有 ${overflow} 个地址, 完整列表请到右侧面板查看</div>`
        : '';
    return `
        <div style="font-size:13px;line-height:20px;color:#1f2937">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
                <span style="width:8px;height:8px;border-radius:50%;background:${AOI_DETAIL_COLORS.relatedAddress};flex-shrink:0"></span>
                <span style="font-weight:600;color:#111827;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;flex:1;white-space:nowrap">${cluster.lat.toFixed(7)}, ${cluster.lng.toFixed(7)}</span>
                <button type="button" class="aoi-popup-close" aria-label="关闭">×</button>
            </div>
            <div style="display:flex;flex-direction:column;gap:5px">${lines}</div>
            ${overflowHtml}
        </div>
    `;
}

/** 转义最小集 (地址里不应有这些字符, 防御一下 XSS / 标签被吞)。 */
function escapeHtml(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Mapbox 关联地址 cluster marker - 命令式创建 + 全局唯一 popup 管理。
 *
 * <p>跟 Google 那侧的体验保持一致: 多条地址聚合到一个点, 点击弹 popup 看明细。
 * 用全局 activePopupRef 保证同时只有一个 popup 开着 (点 marker A → 自动关 marker B 的 popup), 跟世界级地图 UX 对齐。
 */
function addMapboxAddressClusterMarker(
    map: mapboxgl.Map,
    cluster: AddressCluster,
    markersRef: React.MutableRefObject<mapboxgl.Marker[]>,
    activePopupRef: React.MutableRefObject<mapboxgl.Popup | null>,
) {
    const el = document.createElement('div');
    const isMulti = cluster.addresses.length > 1;
    const size = isMulti ? 12 : 10;
    el.style.cssText = `
        width:${size}px;height:${size}px;border-radius:50%;
        background:${AOI_DETAIL_COLORS.relatedAddress};
        border:1.5px solid #fff;cursor:pointer;
    `;
    el.title = isMulti
        ? `${cluster.addresses.length} 条地址在此点`
        : (cluster.addresses[0]?.fullStandardAddress ?? '');

    const marker = new mapboxgl.Marker(el)
        .setLngLat([cluster.lng, cluster.lat])
        .addTo(map);
    el.dataset.markerKey = `cluster-${cluster.key}`;

    el.addEventListener('click', (e) => {
        e.stopPropagation();
        // toggle: 同一个 cluster 的 popup 已开 → 关掉
        const curKey = activePopupRef.current?.getElement()?.dataset.markerKey;
        if (curKey === el.dataset.markerKey) {
            activePopupRef.current?.remove();
            activePopupRef.current = null;
            return;
        }
        // 关旧开新
        activePopupRef.current?.remove();
        const popup = new mapboxgl.Popup({
            offset: 14,
            closeButton: false,  // 我们 popup HTML 里自带 X
            closeOnClick: true,  // 点地图空白处自动关 (mapbox 默认)
            maxWidth: 'none',    // 取消 240px 默认限制
            className: 'aoi-popup',
        }).setHTML(buildAddressClusterPopupHtml(cluster)).setLngLat([cluster.lng, cluster.lat]).addTo(map);
        const popupEl = popup.getElement();
        if (popupEl) popupEl.dataset.markerKey = el.dataset.markerKey ?? '';
        wirePopupCloseButton(popup);
        activePopupRef.current = popup;
        popup.on('close', () => {
            if (activePopupRef.current === popup) activePopupRef.current = null;
        });
    });
    markersRef.current.push(marker);
}

export default AoiDetailMap;
